import { CssGrid, Orientation, GapSize, Position } from "./cssGrid";
import { FileUploadButton, FileUploadButtonProps } from "./fileUploadButton";

import { useIsMobile } from "../hooks/useResponsiveBreakpoint";

export const MAX_BROWSER_UPLOAD_BYTES = 16 * 1024 * 1024;

export const buildFileBufferObject = async (
    uploadedFile: File,
): Promise<FileBufferObject> => {
    if (uploadedFile.size > MAX_BROWSER_UPLOAD_BYTES) {
        throw new Error(
            `File exceeds the ${MAX_BROWSER_UPLOAD_BYTES}-byte browser limit`,
        );
    }
    const buffer = await uploadedFile.arrayBuffer();
    if (buffer.byteLength > MAX_BROWSER_UPLOAD_BYTES) {
        throw new Error(
            `File exceeds the ${MAX_BROWSER_UPLOAD_BYTES}-byte browser limit`,
        );
    }
    return {
        filename: uploadedFile.name,
        data: new Uint8Array(buffer),
    };
};

const getFieldFileUploadLabel = (value: FileBufferObject | null): string => {
    return value ? value.filename : "No file chosen";
};

export const BufferFileUpload = ({
    onFileSelect,
    uploadedFile,
    label,
    ...remainingProps
}: BufferFileUploadProps): JSX.Element => {
    const isMobile = useIsMobile();
    return (
        <CssGrid
            orientation={
                isMobile ? Orientation.vertical : Orientation.horizontal
            }
            gap={GapSize.medium}
            alignItems={Position.center}
            justifyContent={isMobile ? Position.stretch : Position.start}
        >
            <FileUploadButton
                variant="contained"
                {...remainingProps}
                onFileSelect={async (fileList: FileList | null) => {
                    if (!fileList) return;

                    const file = fileList[0];

                    if (file) {
                        try {
                            const bufferObject =
                                await buildFileBufferObject(file);
                            onFileSelect(bufferObject);
                        } catch (err) {
                            console.error(
                                "An error occurred while processing the file",
                                err,
                            );
                        }
                    }
                }}
            >
                <CssGrid
                    orientation={Orientation.horizontal}
                    gap={GapSize.medium}
                    alignItems={Position.center}
                >
                    {label || "Choose File"}
                </CssGrid>
            </FileUploadButton>
            <div>{getFieldFileUploadLabel(uploadedFile)}</div>
        </CssGrid>
    );
};

export interface FileBufferObject {
    filename: string;
    data: Uint8Array;
}

interface BufferFileUploadProps
    extends Omit<FileUploadButtonProps, "onFileSelect" | "children"> {
    onFileSelect: (file: FileBufferObject | null) => void;
    uploadedFile: FileBufferObject | null;
    label?: string;
}
